import { imageTransformRuntimeOptions, resizedAssetUrl } from "@playbrain/content-types"
import type { SupportedLocale } from "@site-types/content"
import { localizedPath } from "@utils/locale"

export type HeadMeta = {
  name?: string
  property?: string
  content?: string
  /**
   * The document title.
   *
   * TanStack renders <title> from an entry in `meta`, not from a `title` key
   * beside it — so a head that only set `title` produced no tag at all. That
   * is why a hardcoded <title> sat in the root document for so long, and why
   * removing it left every page untitled until the title moved in here.
   */
  title?: string
}

export type HeadLink = {
  rel: string
  href: string
  as?: string
  fetchPriority?: "high" | "low" | "auto"
  hrefLang?: string
  imageSizes?: string
  imageSrcSet?: string
}

export type PageHead = {
  title: string
  meta: HeadMeta[]
  links: HeadLink[]
}

type PageHeadOptions = {
  locale: SupportedLocale
  path: string
  title: string
  description?: string
  ogTitle?: string
  ogDescription?: string
  image?: string
  type?: "article" | "website"
  author?: string
  publisher?: string
  siteName?: string
  twitterCard?: string
  twitterSite?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  robots?: string
  stage?: string
}

const SITE_ORIGIN = "https://playbrain.com"
const DEFAULT_AUTHOR = "PLAYBRAIN"
const DEFAULT_DESCRIPTION =
  "PLAYBRAIN engages in game marketing, influencer marketing, and the development and operation of game-related web products and e-commerce services. Additionally, the production and management of esports events are also major aspects of our business."
const DEFAULT_IMAGE_KEY = "content-assets/defaults/playbrain-og-default-1200x630.png"
const DEFAULT_STATIC_IMAGE_PATH = "/images/og_image.png"

export function publicAssetBaseUrl(
  stage = import.meta.env.PUBLIC_STAGE || import.meta.env.VITE_PUBLIC_STAGE
): string {
  const configured = import.meta.env.PUBLIC_ASSET_BASE_URL || import.meta.env.VITE_PUBLIC_ASSET_BASE_URL
  if (configured) return configured.replace(/\/$/, "")
  if (stage === "staging") return "https://staging.asset.playbrain.com"
  return "https://dev.asset.playbrain.com"
}

function optimizedOgImage(url: string, stage?: string): string {
  const objectKey = objectKeyFromAssetUrl(url)
  if (!objectKey) return url
  return (
    resizedAssetUrl({ objectKey }, "markdown", {
      ...imageTransformRuntimeOptions(import.meta.env),
      stage,
    }) ?? url
  )
}

function siteOriginForStage(
  stage = import.meta.env.PUBLIC_STAGE || import.meta.env.VITE_PUBLIC_STAGE
): string {
  if (stage === "dev") return "https://dev.playbrain.com"
  if (stage === "staging") return "https://staging.playbrain.com"
  return SITE_ORIGIN
}

/**
 * Share images have to be absolute.
 *
 * A route passing its own file from `public/` gave og:image a root-relative
 * path, which every scraper reads against its own origin and drops. The
 * default image was absolute only because it is built from a URL; a page's
 * own image is now held to the same rule.
 */
function absoluteImageUrl(url: string, stage?: string): string {
  if (!url.startsWith("/")) return url
  return new URL(url, siteOriginForStage(stage)).toString()
}

function defaultStaticImageUrl(stage?: string): string {
  return new URL(DEFAULT_STATIC_IMAGE_PATH, siteOriginForStage(stage)).toString()
}

function isMissingDefaultAssetImage(url: string | undefined, stage?: string): boolean {
  if (!url) return true
  if (url === "og_image.png" || url === "/images/og_image.png") return true
  return objectKeyFromAssetUrl(stageAssetUrl(url, stage)) === DEFAULT_IMAGE_KEY
}

function stageAssetUrl(url: string, stage?: string): string {
  if (
    !url.startsWith("https://dev.asset.playbrain.com/") &&
    !url.startsWith("https://dev.assets.playbrain.com/") &&
    !url.startsWith("https://assets.dev.playbrain.com/") &&
    !url.startsWith("https://staging.asset.playbrain.com/") &&
    !url.startsWith("https://assets.staging.playbrain.com/") &&
    !url.startsWith("https://asset.playbrain.com/") &&
    !url.startsWith("https://assets.playbrain.com/")
  ) {
    return url
  }

  const assetUrl = new URL(url)
  return `${publicAssetBaseUrl(stage)}${assetUrl.pathname}${assetUrl.search}${assetUrl.hash}`
}

function objectKeyFromAssetUrl(url: string) {
  if (
    !url.startsWith("https://dev.asset.playbrain.com/") &&
    !url.startsWith("https://dev.assets.playbrain.com/") &&
    !url.startsWith("https://assets.dev.playbrain.com/") &&
    !url.startsWith("https://staging.asset.playbrain.com/") &&
    !url.startsWith("https://assets.staging.playbrain.com/") &&
    !url.startsWith("https://asset.playbrain.com/") &&
    !url.startsWith("https://assets.playbrain.com/")
  ) {
    return null
  }

  return new URL(url).pathname.replace(/^\/+/, "")
}

function lighthouseIndexableEnabled(): boolean {
  return (
    import.meta.env.PUBLIC_LIGHTHOUSE_INDEXABLE === "true" ||
    import.meta.env.VITE_PUBLIC_LIGHTHOUSE_INDEXABLE === "true"
  )
}

export function robotsContent(
  stage = import.meta.env.PUBLIC_STAGE || import.meta.env.VITE_PUBLIC_STAGE
): string {
  return stage === "prod" || lighthouseIndexableEnabled() ? "index,follow" : "noindex,nofollow"
}

export function canonicalUrl(path: string, origin = SITE_ORIGIN): string {
  const url = new URL(path || "/", origin)
  url.search = ""
  url.hash = ""
  const pathname = url.pathname.length > 1 ? url.pathname.replace(/\/+$/, "") : url.pathname
  return new URL(pathname, origin).toString()
}

export function buildPageHead({
  locale,
  path,
  title,
  description = DEFAULT_DESCRIPTION,
  ogTitle,
  ogDescription,
  image,
  type = "website",
  author = DEFAULT_AUTHOR,
  publisher = DEFAULT_AUTHOR,
  siteName = "PLAYBRAIN Inc.",
  twitterCard = "summary_large_image",
  twitterSite,
  twitterTitle,
  twitterDescription,
  twitterImage,
  robots,
  stage,
}: PageHeadOptions): PageHead {
  const defaultImage = defaultStaticImageUrl(stage)
  const canonical = canonicalUrl(path)
  const alternateEn = canonicalUrl(localizedPath(path, "en"))
  const alternateJa = canonicalUrl(localizedPath(path, "ja"))
  const resolvedOgTitle = ogTitle || title
  const resolvedOgDescription = ogDescription || description
  const pageImage = isMissingDefaultAssetImage(image, stage) ? defaultImage : image
  const resolvedImage = optimizedOgImage(
    absoluteImageUrl(stageAssetUrl(pageImage || defaultImage, stage), stage),
    stage
  )
  const resolvedTwitterTitle = twitterTitle || resolvedOgTitle
  const resolvedTwitterDescription = twitterDescription || resolvedOgDescription
  const pageTwitterImage = isMissingDefaultAssetImage(twitterImage, stage) ? resolvedImage : twitterImage
  const resolvedTwitterImage = optimizedOgImage(
    absoluteImageUrl(stageAssetUrl(pageTwitterImage || resolvedImage, stage), stage),
    stage
  )

  return {
    title,
    meta: [
      { title },
      { name: "description", content: description },
      { name: "author", content: author },
      { name: "publisher", content: publisher },
      { name: "robots", content: robots || robotsContent(stage) },
      { property: "og:title", content: resolvedOgTitle },
      { property: "og:description", content: resolvedOgDescription },
      { property: "og:url", content: canonical },
      { property: "og:image", content: resolvedImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: locale },
      { property: "og:site_name", content: siteName },
      { property: "og:type", content: type },
      { name: "twitter:card", content: twitterCard },
      ...(twitterSite ? [{ name: "twitter:site", content: twitterSite }] : []),
      { name: "twitter:title", content: resolvedTwitterTitle },
      { name: "twitter:description", content: resolvedTwitterDescription },
      { name: "twitter:image", content: resolvedTwitterImage },
    ] satisfies HeadMeta[],
    links: [
      { rel: "canonical", href: canonical },
      { rel: "alternate", href: alternateEn, hrefLang: "en" },
      { rel: "alternate", href: alternateJa, hrefLang: "ja" },
    ] satisfies HeadLink[],
  }
}
